<template>
	<div class="c-hcp-consent-area">
		<DsText
			text-style="regular"
			text-type="body-lg"
			html-element="div"
			class="c-hcp-consent-area__title"
		>
			{{ vm.CurrentBlock.ConsentElement.Headline }}
		</DsText>

		<div
			v-if="vm.CurrentBlock.ConsentElement.UspItems?.length"
			class="c-hcp-consent-area__list"
		>
			<div
				v-for="item in vm.CurrentBlock.ConsentElement.UspItems"
				:key="item"
				class="c-hcp-consent-area__list-item"
			>
				<DsIcon :icon-name="item.Icon" size="md" />
				<DsText
					text-style="medium"
					text-type="body-md"
					html-element="div"
				>
					{{ item.Text }}
				</DsText>
			</div>
		</div>

		<div class="c-hcp-consent-area__content">
			<DsText
				v-if="vm.CurrentBlock.ConsentElement.AdditionalInformations"
				text-style="regular"
				text-type="body-lg"
				html-element="div"
				class="c-hcp-consent-area__information"
			>
				{{ vm.CurrentBlock.ConsentElement.AdditionalInformations }}
			</DsText>

			<FormKit
				id="consentForm"
				type="form"
				:actions="false"
				:messages="false"
				:incomplete-message="false"
				@submit="onSubmit"
			>
				<FormKit
					type="checkbox"
					:label="vm.CurrentBlock.ConsentElement.ConsentHeader"
					name="consent"
					validation="accepted"
					validation-visibility="dirty"
					:validation-messages="{
						accepted:
							vm.CurrentBlock.ConsentElement
								.ConsentRequiredErrorText,
					}"
					class="c-hcp-consent-area__checkbox"
				/>

				<DsText
					text-style="regular"
					text-type="body-sm"
					html-element="div"
					class="c-hcp-consent-area__helper"
				>
					<div
						class="c-hcp-consent-area__helper-content"
						v-html="consentDescriptionHtml"
					/>
				</DsText>

				<div class="c-hcp-consent-area__actions">
					<DsButton variant="primary" type="submit">
						{{ vm.CurrentBlock.ConsentElement.SignupButtonText }}
					</DsButton>

					<div class="c-hcp-consent-area__account">
						<DsText
							v-if="
								vm.CurrentBlock.ConsentElement
									.AlreadyHaveAccountText
							"
							text-style="regular"
							text-type="body-sm"
							html-element="div"
							class="c-hcp-consent-area__have-account"
						>
							{{
								vm.CurrentBlock.ConsentElement
									.AlreadyHaveAccountText
							}}
						</DsText>
						<DsButton
							:href="vm.BaseViewModel.LoginUri"
							variant="link"
						>
							{{ vm.CurrentBlock.ConsentElement.LogInText }}
						</DsButton>
					</div>
				</div>
			</FormKit>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { FormKit } from '@formkit/vue';
import { DsText, DsButton, DsIcon } from '~/@coloplast/design-system';
import { track_hcp_signup_begin } from 'shared/services/tracking/formTrackingService';

type ConsentForm = {
	consent: boolean;
};

const props = defineProps({
	vm: {
		type: Object,
		required: true,
	},
});

const consentDescriptionHtml = computed(() => {
	const description = props.vm.CurrentBlock.ConsentElement.ConsentDescription;

	if (!description) {
		return '';
	}

	if (description.includes('$link$')) {
		return description.replace(
			'$link$',
			`<a href="${props.vm.BaseViewModel.ConsentPageUrl}" target="_blank">${props.vm.CurrentBlock.ConsentElement.ConsentLinkText}</a>`,
		);
	}

	return description;
});

const onSubmit = (data: ConsentForm) => {
	if (!data.consent) {
		return;
	}

	globalThis.location.href = props.vm.BaseViewModel.SignUpUrl;
};

onMounted(() => {
	track_hcp_signup_begin();
});
</script>

<style lang="scss" scoped>
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-hcp-consent-area {
	display: flex;
	flex-direction: column;
	gap: ds-gap(7);

	&__list {
		padding-block: ds-space(6);
		display: flex;
		flex-direction: column;
		gap: ds-gap(6);

		&-item {
			display: flex;
			flex-direction: row;
			gap: ds-gap(6);
		}

		& .ds-icon {
			color: ds-color(fg, brand);
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
	}

	&__information {
		margin-bottom: ds-space(11);
	}

	&__helper {
		margin: ds-space(3) ds-space(8) ds-space(4);
		padding-inline-start: ds-gap(5);
		color: ds-color(fg, muted);

		&-content {
			display: inline-block;
		}

		:deep(p) {
			margin: 0;
		}

		:deep(.ds-button) {
			padding: 0 ds-space(2);
		}
	}

	&__actions {
		margin-block-start: ds-space(10);
		display: flex;
		flex-direction: column;
		gap: ds-gap(5);

		& > :deep(.ds-button) {
			@media (max-width: ds-breakpoint('md')) {
				width: 100%;
				justify-content: center;
			}
		}
	}

	&__account {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: ds-gap(1);

		:deep(.ds-button) {
			padding: 0;
		}

		@media (max-width: ds-breakpoint('md')) {
			justify-content: center;
		}
	}

	:deep(.formkit-form) {
		padding-block: ds-space(4);
		margin-bottom: ds-space(10);
	}

	:deep(.formkit-outer) {
		margin: 0;

		.formkit-wrapper {
			max-width: 100%;
			align-items: flex-start;

			.formkit-label {
				color: ds-color(fg, default);
			}
		}

		.formkit-inner {
			gap: ds-gap(5);
		}

		.formkit-messages {
			margin-block-start: ds-space(3);
			margin-inline-start: ds-space(8);
			padding-inline-start: ds-gap(5);
			padding-block-start: 0;
		}

		.formkit-message {
			padding: 0;
			font-size: var(--typography-size-body-body-SM, 16px);
			line-height: var(--typography-line-height-body-body-SM, 24px);
		}
	}
}
</style>
